import React from "react"
import { Modal, StyleSheet } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { useStyle } from "react-native-style-utilities"

import { renderElementOrComponent } from "@/components/renderElementOrComponent"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { Overlay } from "@/theme/Overlay"

import { useIsDarkMode } from "./AppThemeContextProvider"
import { Box } from "./Box"
import { AppIcons } from "./icons"
import { Pressable } from "./Pressable"
import { AppText } from "./Text"
import type { SpaceToken } from "./theme"
import { SPACING } from "./theme"
import { COLORS } from "./theme/colors"
import { useDisableScroll } from "./useDisableScroll"
import { View } from "./View"

import type { StyleProp, ViewStyle } from "react-native"
import type { ButtonProps, PressableState } from "./Button"
import type { IconProp } from "./icons"
import type { PressableProps } from "./Pressable"
import type { ViewProps } from "./View"

import type { ElementOrComponent } from "@/base/types"

export interface MenuProps extends ViewProps {
  isOpen: boolean
  onClose: () => void
}

export function Menu({
  children,
  isOpen,
  onClose,
  style,
  ...rest
}: MenuProps) {
  useDisableScroll()

  const isDark = useIsDarkMode()

  const { bottom, top } = useSafeAreaInsets()

  return (
    <Modal
      transparent
      animationType={"fade"}
      visible={isOpen}
      onRequestClose={onClose}
    >
      <Overlay onPress={onClose}>
        <View
          role={"menu"}
          style={useStyle(
            () => [
              styles.menuContent,
              isDark ? styles.menuContentDark : undefined,
              { marginBottom: bottom, marginTop: top },
              style,
            ],
            [bottom, isDark, style, top],
          )}
          {...rest}
        >
          {children}
        </View>
      </Overlay>
      {/* <MenuContent
        isOpen={isOpen}
        style={contentStyle}
        onClose={onClose}
        {...rest}
      /> */}
    </Modal>
  )
}

export interface MenuItemProps
  extends Pick<ButtonProps, "IconComponent" | "id" | "text"> {
  EndComponent?: ElementOrComponent
  EndIconComponent?: IconProp
  color?: string
  style?: StyleProp<ViewStyle>
}

export const MenuItem = React.forwardRef<typeof View, MenuItemProps>(
  function MenuItem(
    { color, EndComponent, EndIconComponent, IconComponent, id, style, text },
    ref,
  ) {
    let endElement: React.JSX.Element | null = null
    if (Boolean(EndIconComponent)) {
      endElement = renderIconFromIconProp(EndIconComponent)
    } else if (EndComponent) {
      endElement = renderElementOrComponent(EndComponent)
    }
    return (
      <View
        // @ts-expect-error figure out typing for refs
        ref={ref}
        id={id}
        role={"menuitem"}
        style={useStyle(() => [styles.menuItemContainer, style], [style])}
      >
        <Box mr={"$2"}>
          {renderIconFromIconProp(
            IconComponent,
            typeof color === "string" ? { color } : {},
          )}
        </Box>
        {typeof text === "string" ? (
          <AppText color={color}>{text}</AppText>
        ) : (
          text
        )}
        {endElement ? <Box ml={"auto"}>{endElement}</Box> : null}
      </View>
    )
  },
)
const styles = StyleSheet.create({
  menuContent: {
    backgroundColor: COLORS.$paper.light,
    borderRadius: 10,
    elevation: 5,
    marginLeft: "auto",
    maxHeight: "80%",
    maxWidth: 400,
    overflow: "scroll",
    padding: 20,
    zIndex: 100,
  },
  menuContentDark: {
    backgroundColor: COLORS.$paper.dark,
  },
  menuItemContainer: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    paddingVertical: SPACING.$3,
  },
  menuItemHovered: {
    opacity: 0.7,
  },
  menuItemPressed: {
    opacity: 0.5,
  },
})

interface MenuItemButtonProps
  extends PressableProps,
    Omit<MenuItemProps, "style"> {
  px?: SpaceToken
}

export const MenuItemButton = React.forwardRef<
  typeof View,
  MenuItemButtonProps
>(function MenuItemButton(
  { color, EndIconComponent, IconComponent, id, px, style, text, ...rest },
  ref,
) {
  const isDisabled = rest.disabled === true
  return (
    <Pressable
      ref={ref}
      accessibilityState={{ disabled: isDisabled }}
      disabled={isDisabled}
      style={(state) => {
        const { hovered = false, pressed } = state as PressableState
        return [
          { paddingHorizontal: SPACING[px ?? "$0"] },

          pressed
            ? styles.menuItemPressed
            : hovered
              ? styles.menuItemHovered
              : undefined,
          typeof style === "function" ? style(state) : style,
        ]
      }}
      {...rest}
    >
      <MenuItem
        EndIconComponent={EndIconComponent ?? AppIcons.ListItemEnd}
        IconComponent={IconComponent}
        color={color}
        id={id}
        text={text}
      />
    </Pressable>
  )
})

